/**
 * Deprecation and retirement, as a person performs them. LIFE-01's product half.
 *
 * WHY THIS IS A SEPARATE FILE
 *   `views/actions.ts` is the one place every `data-action` is registered, and it
 *   is also the file every screen's handlers share. These four handlers are kept
 *   here and registered by one call from there, so this lane's work is a file a
 *   reader can find and review without reading the editor, the review queue and
 *   the consent screen to reach it. Nothing about the registration model changes:
 *   `app.action` is the same registry either way.
 *
 * TWO CONTROLS, NOT ONE
 *   `active -> deprecated` keeps a skill serving and starts warning; `deprecated
 *   -> retired` removes it from discovery and blocks consumption of release ids
 *   that used to resolve. They are not two settings of one thing — they are
 *   different consequences for whoever depends on the skill — so the screen
 *   offers two buttons whose labels say which is which, and retirement asks for
 *   confirmation that deprecation does not.
 *
 * NEITHER TRANSITION IS REVERSIBLE IN THIS SCHEMA, AND THE SCREEN SAYS SO
 *   `app.set_skill_lifecycle` permits `active -> deprecated` and
 *   `deprecated -> retired` and nothing else (`0033_lifecycle_rpcs.sql`); there
 *   is no tombstone and no reinstatement. A confirmation dialog on a destructive
 *   act that does not warn it is permanent is a dialog that lies by omission.
 *
 * NO SUCCESS IS REPORTED BEFORE IT IS READ BACK
 *   The write's 200 is the server's word that the row moved. The screen re-reads
 *   the skill and only then announces it — the same rule `editor:save` follows
 *   before it drops the kept copy of unsaved text. A refusal leaves the reason in
 *   the box, because a reason the person typed and a transition that did not
 *   happen are not a thing to clean up on the way out.
 */
import type { Failure } from '../api/client.js';
import * as api from '../api/queries.js';
import type { App } from '../app.js';
import { presentFailure } from '../ui/components.js';
import type { SkillData } from './skill.js';

function report(app: App, failure: Failure): void {
  const presented = presentFailure(failure);
  app.notify('bad', `${presented.title}. ${presented.body}`);
}

/**
 * The skill this screen is about, or null when it is not a skill screen.
 *
 * Read from the ROUTE rather than from the screen data on purpose: a handler
 * that fires against whatever the last screen happened to hold is a handler that
 * can retire a different skill from the one the person is looking at.
 */
function currentSkillId(app: App): string | null {
  const route = app.state.route;
  return route.name === 'skill' ? route.id : null;
}

function currentLifecycle(app: App): string | null {
  if (app.state.screen.kind !== 'ready') return null;
  const data = app.state.screen.data as SkillData | null;
  return data === null || data === undefined ? null : data.skill.lifecycle;
}

/**
 * Both controls, through one write.
 *
 * `to` is the ONLY thing that differs between them, and it is the thing the path
 * names. A single control carrying a state field would have to decide the chain
 * in the browser, which is the state machine the database owns.
 */
async function transition(app: App, to: 'deprecated' | 'retired'): Promise<void> {
  const skillId = currentSkillId(app);
  if (skillId === null) return;

  // Refused here rather than at the route, for the reason
  // `review:request-changes` refuses a blank note: a blank reason has exactly
  // one possible outcome, and replacing "say why" with "reason required" is the
  // same instruction and a worse experience. The route refuses it too, and the
  // database refuses it a third time.
  const reason = app.state.lifecycleReason.trim();
  if (reason === '') {
    app.notify(
      'bad',
      'A lifecycle change needs a reason: it becomes the audit record of why this skill is going away.',
    );
    return;
  }

  const title = skillTitle(app);
  if (to === 'retired') {
    const ok = globalThis.confirm(
      `Retire ${title}? It leaves the catalog and can no longer be retrieved, by anyone, ` +
        'including by a release id someone kept. This cannot be undone.',
    );
    if (!ok) return;
  }

  const moved = await api.setSkillLifecycle(
    app.client,
    { skillId, to, reason, replacementSkillId: null },
    crypto.randomUUID(),
  );
  if (!moved.ok) {
    // The reason stays in the box. Nothing was written, so nothing is cleared.
    report(app, moved.failure);
    return;
  }

  // The reason is now in the audit trail, where it belongs. Leaving it in the box
  // invites the next press to reuse a sentence that described a different
  // decision.
  app.patch({ lifecycleReason: '' });
  await app.reload();

  if (currentLifecycle(app) !== to) {
    // The write reported success and the screen does not show the new state.
    // That is not a success, and saying so would be the lie this project keeps
    // refusing; the screen's own failure branch renders the reason.
    report(app, {
      kind: 'server',
      detail:
        `The transition to ${to} was accepted and the answer says ${to}, but reloading this ` +
        'screen does not show it. Nothing is claimed about the skill\'s current state.',
      requestId: null,
    });
    return;
  }

  app.notify(
    'ok',
    to === 'deprecated'
      ? `${title} is deprecated. It still serves, and every read of it now carries a warning.`
      : `${title} is retired. It has left the catalog and can no longer be retrieved.`,
  );
}

function skillTitle(app: App): string {
  if (app.state.screen.kind !== 'ready') return 'this skill';
  const data = app.state.screen.data as SkillData | null;
  return data?.skill.title ?? 'this skill';
}

export function registerLifecycleActions(app: App): void {
  app.action('field:lifecycle.reason', (element) => {
    // `App.onField` only resolves a `data-field` handler for an input, a
    // textarea or a select (`app.ts:642-649`), so `value` is a string by the
    // time this runs. The narrowing is written as a read rather than as an
    // `instanceof` chain because the chain cannot fail in a browser and would
    // make the handler untestable without a DOM.
    const field = element as Partial<HTMLTextAreaElement>;
    app.patch({ lifecycleReason: typeof field.value === 'string' ? field.value : '' });
  });
  app.action('skill:lifecycle.deprecate', () => transition(app, 'deprecated'));
  app.action('skill:lifecycle.retire', () => transition(app, 'retired'));
}
